import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SettingsIcon from '@mui/icons-material/Settings';
import TuneIcon from '@mui/icons-material/Tune';
import DeleteIcon from '@mui/icons-material/Delete';
import { DataFeedRowData } from '../../../types/generatedTypes';

interface ToolbarButton {
  variant: 'text';
  label: string;
  onClick: () => void;
  icon: React.ReactElement;
  disabled: boolean;
}

interface UseToolbarConfigurationProps {
  selectedRows: Array<number | string>;
  displayData: DataFeedRowData[];
  processedRows: Array<{
    id: number | string;
    _array_index?: number;
    _row_index?: number;
  }>;
  viewMode: 'raw' | 'processed';
  isUploadingToIllio: boolean;
  onUploadExcel: () => void;
  onUploadKasefet: () => void;
  onUploadToIllio: (rowIndices: number[]) => void;
  onDeleteRows: (rows: Array<number | string>) => void;
  viewButtons: ToolbarButton[];
}

export const useToolbarConfiguration = ({
  selectedRows,
  displayData,
  processedRows,
  viewMode,
  isUploadingToIllio,
  onUploadExcel,
  onUploadKasefet,
  onUploadToIllio,
  onDeleteRows,
  viewButtons,
}: UseToolbarConfigurationProps) => {
  const navigate = useNavigate();

  const toolbarButtons = useMemo(
    () => [
      ...viewButtons,
      {
        variant: 'text' as const,
        label: 'Upload Excel',
        color: 'secondary',
        onClick: onUploadExcel,
        icon: <CloudUploadIcon />,
        disabled: isUploadingToIllio,
        inMenu: true,
      },
      {
        variant: 'text' as const,
        label: 'Upload Vault',
        color: 'secondary',
        onClick: onUploadKasefet,
        icon: <CloudUploadIcon />,
        disabled: isUploadingToIllio,
        inMenu: true,
      },
      {
        variant: 'contained' as const,
        label: 'Upload to Illio',
        onClick: () => {
          // Get array indices for selected processed rows
          // _array_index represents the position in processed_file.processed_rows array
          const rowIndicesToUpload = processedRows
            .filter(row => selectedRows.includes(row.id))
            .map(row => row._array_index as number)
            .filter(index => index !== undefined);

          onUploadToIllio(rowIndicesToUpload);
        },
        icon: <CloudUploadIcon />,
        // Upload only enabled in processed view with selected rows
        disabled:
          selectedRows.length === 0 ||
          isUploadingToIllio ||
          viewMode !== 'processed',
      },
      {
        variant: 'text' as const,
        label: 'Delete Rows',
        color: 'secondary',

        onClick: () => onDeleteRows(selectedRows),
        icon: <DeleteIcon />,
        disabled: selectedRows.length === 0 || isUploadingToIllio,
      },
      {
        variant: 'text' as const,
        label: 'Configuration',
        color: 'secondary',

        onClick: () => navigate('/danel/config'),
        icon: <SettingsIcon />,
        disabled: isUploadingToIllio,
      },
      {
        variant: 'text' as const,
        label: 'Illio Configuration',
        color: 'secondary',
        onClick: () => navigate('/danel/illio-config'),
        icon: <TuneIcon />,
        disabled: isUploadingToIllio,
      },
    ],
    [
      viewButtons,
      selectedRows,
      displayData,
      processedRows,
      viewMode,
      isUploadingToIllio,
      onUploadExcel,
      onUploadKasefet,
      onUploadToIllio,
      onDeleteRows,
      navigate,
    ]
  );

  return { toolbarButtons };
};
